import {
  AggregationOption,
  NormalizationType,
} from "@hightouch/lib/query/visual/types";
import { NormalizationOption } from "src/pages/analytics/types";
import {
  AttributionBasis,
  AttributionWindow,
  IntervalUnit,
} from "src/types/visual";
export { AggregationOption };

export const AggregationOptionsWithColumns = [
  AggregationOption.SumOfProperty,
  AggregationOption.SumOfPropertyPerUser,
  AggregationOption.AverageOfProperty,
  AggregationOption.AverageOfPropertyPerUser,
  AggregationOption.CountDistinctProperty,
];

export const AggregationOptionsWithAttributionMethods = [
  AggregationOption.Count,
  AggregationOption.SumOfProperty,
];

export type MetricAggregationOption = {
  value: AggregationOption;
  label: string;
  description: string;
};

export const AggregationOptions = [
  {
    value: AggregationOption.Count,
    label: "Total events",
    description: "Total number of events performed",
  },
  {
    value: AggregationOption.UniqueUsers,
    label: "Unique members",
    description: "Number of members who performed an event",
  },
  {
    value: AggregationOption.SumOfProperty,
    label: "Sum of property",
    description: "Sum of a property value across all events",
  },
  {
    value: AggregationOption.AverageOfProperty,
    label: "Average of property",
    description: "Average value of a property across all events",
  },
  {
    value: AggregationOption.CountDistinctProperty,
    label: "Distinct count of property",
    description: "Count of distinct property values across all events",
  },
  {
    value: AggregationOption.SumOfPropertyPerUser,
    label: "Sum of property per member",
    description:
      "Sum of a property value for each member, averaged across all members",
  },
  {
    value: AggregationOption.AverageOfPropertyPerUser,
    label: "Average of property per member",
    description:
      "Average of a property value for each member, averaged across all members",
  },
  {
    value: AggregationOption.PercentOfAudience,
    label: "% of audience",
    description: "Percentage of members of an audience who performed an event",
  },
];

export const UserAggregationOptions = [
  {
    value: AggregationOption.Count,
    label: "Total members",
    description: "Total number of members",
  },
  {
    value: AggregationOption.SumOfProperty,
    label: "Sum of property",
    description: "Sum of a property value across all members",
  },
  {
    value: AggregationOption.AverageOfProperty,
    label: "Average of property",
    description: "Average value of a property across all members",
  },
  {
    value: AggregationOption.CountDistinctProperty,
    label: "Distinct count of property",
    description: "Count of distinct property values across all members",
  },
  {
    value: AggregationOption.SumOfPropertyPerUser,
    label: "Sum of property per member",
    description:
      "Sum of a property value for each member, averaged across all members",
  },
  {
    value: AggregationOption.AverageOfPropertyPerUser,
    label: "Average of property per member",
    description:
      "Average of a property value for each member, averaged across all members",
  },
  {
    value: AggregationOption.PercentOfAudience,
    label: "% of audience",
    description: "Percentage of members in an audience",
  },
];

export const RelatedModelAggregationOptions = [
  {
    value: AggregationOption.Count,
    label: "Total rows",
    description: "Total number of related rows",
  },
  {
    value: AggregationOption.UniqueUsers,
    label: "Unique members",
    description: "Number of members with a related row",
  },
  {
    value: AggregationOption.SumOfProperty,
    label: "Sum of property",
    description: "Sum of a property value across all related rows",
  },
  {
    value: AggregationOption.AverageOfProperty,
    label: "Average of property",
    description: "Average value of a property across all related rows",
  },
  {
    value: AggregationOption.CountDistinctProperty,
    label: "Distinct count of property",
    description: "Count of distinct property values across all related rows",
  },
  {
    value: AggregationOption.SumOfPropertyPerUser,
    label: "Sum of property per member",
    description:
      "Sum of a property value for each member, averaged across all members",
  },
  {
    value: AggregationOption.AverageOfPropertyPerUser,
    label: "Average of property per member",
    description:
      "Average of a property value for each member, averaged across all members",
  },
  {
    value: AggregationOption.PercentOfAudience,
    label: "% of audience",
    description: "Percentage of members in an audience who have a related row",
  },
];

export const ATTRIBUTION_BASIS_OPTIONS = [
  {
    value: AttributionBasis.Entry,
    label: "audience entry",
    shortLabel: "after entry",
    description: "The date a user enters the audience",
  },
  {
    value: AttributionBasis.Exit,
    label: "audience exit",
    shortLabel: "after exit",
    description: "The date a user exits the audience",
  },
];

export const DEFAULT_ATTRIBUTION_WINDOW: AttributionWindow = {
  unit: IntervalUnit.Day,
  quantity: 0,
  basis: AttributionBasis.Exit,
};

export const TIME_OPTIONS = [
  {
    value: IntervalUnit.Hour,
    label: "hour(s)",
    textLabel: "hour",
  },
  {
    value: IntervalUnit.Day,
    label: "day(s)",
    textLabel: "day",
  },
  {
    value: IntervalUnit.Week,
    label: "week(s)",
    textLabel: "week",
  },
  {
    value: IntervalUnit.Month,
    label: "month(s)",
    textLabel: "month",
  },
];

export const NORMALIZATION_GROUP_OPTIONS: {
  label: string;
  options: NormalizationOption[];
}[] = [
  {
    label: "Size adjustment",
    options: [
      {
        value: NormalizationType.TreatmentSize,
        label: "Normalized to treatment size",
        description:
          "Display non-treatment group results as if they were the same size as the treatment group",
      },
      {
        value: NormalizationType.HundredPercent,
        label: "Normalized to entire population size",
        description:
          "Display all experiment group results as if they were rolled out to the entire population",
      },
    ],
  },
  {
    label: "Lift",
    options: [
      {
        value: NormalizationType.LiftPercent,
        label: "% lift",
        description: "Relative change in outcomes that the treatment drove",
      },
      {
        value: NormalizationType.AbsoluteLift,
        label: "Absolute lift",
        description: "Incremental outcomes that the treatment drove",
      },
    ],
  },
  {
    label: "Rates",
    options: [
      {
        value: NormalizationType.RatePerUser,
        label: "Per-member rate",
        description: "Divide metric by the number of members being measured",
      },
      {
        value: NormalizationType.RatePerInteraction,
        label: "Per-interaction rate",
        description: "Divide metric by the number of interactions sent",
      },
    ],
  },
];

export const isLiftNormalizationType = (
  normalization: NormalizationType | undefined,
) =>
  normalization === NormalizationType.LiftPercent ||
  normalization === NormalizationType.AbsoluteLift;
